/* ==========================================================================
   Parlay — mobile menu science-card spectrum
   --------------------------------------------------------------------------
   Reveals the mobile-menu drawer photo cards on scroll-into-view: draws the
   wavelength spectrum on the Science cards (snippets/parlay-science-card.liquid)
   and triggers the light-sheen sweep on the Shop photo cards. Loaded deferred
   by sections/header.liquid whenever drawer photo cards can appear.

   A canvas inside the closed drawer has zero size. A ResizeObserver tracks
   layout (draw once the drawer opens, redraw on resize); an IntersectionObserver
   gates the animated reveal so it plays when the card is scrolled into view —
   not unseen on open when the science section sits below the drawer fold.
   Engine (wavelengthToColor / ledSpectrum / transmission / draw) is ported
   verbatim from sections/parlay-lens-collection.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var CANVAS_SELECTOR = 'canvas.parlay-science-card__canvas';
  var NAV_CARD_SELECTOR = 'a.parlay-nav-card';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function wavelengthToColor(nm, alpha) {
    var a = typeof alpha === 'number' ? alpha : 1, r = 0, g = 0, b = 0;
    if (nm >= 380 && nm < 440) { r = -(nm - 440) / 60; b = 1; }
    else if (nm >= 440 && nm < 490) { g = (nm - 440) / 50; b = 1; }
    else if (nm >= 490 && nm < 510) { g = 1; b = -(nm - 510) / 20; }
    else if (nm >= 510 && nm < 580) { r = (nm - 510) / 70; g = 1; }
    else if (nm >= 580 && nm < 645) { r = 1; g = -(nm - 645) / 65; }
    else if (nm >= 645 && nm <= 780) { r = 1; }
    var f = 1;
    if (nm >= 380 && nm < 420) f = 0.3 + (0.7 * (nm - 380)) / 40;
    else if (nm > 700 && nm <= 780) f = 0.3 + (0.7 * (780 - nm)) / 80;
    r = Math.pow(r * f, 0.8); g = Math.pow(g * f, 0.8); b = Math.pow(b * f, 0.8);
    return 'rgba(' + Math.round(r * 255) + ',' + Math.round(g * 255) + ',' + Math.round(b * 255) + ',' + a + ')';
  }

  function ledSpectrum(nm) {
    var bl = Math.exp(-0.5 * Math.pow((nm - 450) / 13, 2)) * 0.95;
    var ph = Math.exp(-0.5 * Math.pow((nm - 552) / 22, 2)) * 0.78;
    var rt = Math.exp(-0.5 * Math.pow((nm - 640) / 30, 2)) * 0.64;
    return Math.min(1, bl + ph + rt);
  }

  function smoothstep(e0, e1, x) {
    var t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  }

  function transmission(nm, s, e) {
    var edge = 14;
    if (nm >= s && nm <= e) return 0;
    if (nm < s) {
      if (nm <= s - edge) return 1;
      return 1 - smoothstep(s - edge, s, nm);
    }
    if (nm >= e + edge) return 1;
    return smoothstep(e, e + edge, nm);
  }

  function draw(canvas, prog) {
    var s = parseFloat(canvas.getAttribute('data-start'));
    var e = parseFloat(canvas.getAttribute('data-end'));
    var rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    var dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    var ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    var w = rect.width, h = rect.height;
    ctx.clearRect(0, 0, w, h);
    var nmMin = 380, nmMax = 780, padB = 1;
    function nmToX(nm) { return ((nm - nmMin) / (nmMax - nmMin)) * w; }
    function valToY(v) { return (h - padB) - v * (h - padB) * 0.9; }
    for (var nm = nmMin; nm < nmMax; nm++) {
      var it = ledSpectrum(nm) * transmission(nm, s, e) * prog;
      if (it < 0.012) continue;
      var x = nmToX(nm), xN = nmToX(nm + 1), yV = valToY(it);
      var gr = ctx.createLinearGradient(x, valToY(0), x, yV);
      gr.addColorStop(0, wavelengthToColor(nm, 0.05));
      gr.addColorStop(0.35, wavelengthToColor(nm, 0.30));
      gr.addColorStop(0.7, wavelengthToColor(nm, 0.7));
      gr.addColorStop(1, wavelengthToColor(nm, 1));
      ctx.fillStyle = gr;
      ctx.fillRect(x, yV, Math.ceil(xN - x) + 1, valToY(0) - yV);
    }
    ctx.beginPath();
    ctx.moveTo(0, valToY(0));
    ctx.lineTo(w, valToY(0));
    ctx.strokeStyle = 'rgba(246,243,236,0.28)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.lineWidth = 1.1;
    for (var nl = nmMin; nl <= nmMax; nl += 1) {
      var il = ledSpectrum(nl) * transmission(nl, s, e) * prog, xl = nmToX(nl), yl = valToY(il);
      if (nl === nmMin) ctx.moveTo(xl, yl); else ctx.lineTo(xl, yl);
    }
    ctx.strokeStyle = 'rgba(246,243,236,0.5)';
    ctx.stroke();
  }

  var progress = new WeakMap();   // canvas -> current animation progress (0..1)
  var observed = new WeakSet();   // canvases already wired up
  var revealed = new WeakSet();   // reveal animation already started
  var onscreen = new WeakSet();   // canvas currently scrolled into view

  function reveal(canvas) {
    if (reduceMotion) {
      progress.set(canvas, 1);
      draw(canvas, 1);
      return;
    }
    var start = null, duration = 850;
    function step(t) {
      if (start === null) start = t;
      var p = Math.min(1, (t - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      progress.set(canvas, eased);
      draw(canvas, eased);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* Animate in once — but only when the card is BOTH laid out (has size, i.e.
     the drawer is open) AND scrolled into view. On short screens the science
     cards sit below the drawer fold, so gating on visibility means the reveal
     plays when the user reaches the section, not unseen on open. */
  function tryReveal(canvas) {
    if (revealed.has(canvas) || !onscreen.has(canvas)) return;
    var rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    revealed.add(canvas);
    var card = canvas.closest('.parlay-science-card');
    if (card) card.classList.add('in');
    reveal(canvas);
    if (viewObserver) viewObserver.unobserve(canvas);
  }

  function paint(canvas) {
    var rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    if (revealed.has(canvas)) {
      draw(canvas, progress.get(canvas) || 1);   // resize → redraw at current progress
    } else {
      tryReveal(canvas);                          // has size now; reveal iff also in view
    }
  }

  /* Reveal trigger: fire the animation when a card scrolls into view — the
     spectrum sweep for Science cards, the light sheen for Shop photo cards. */
  var viewObserver = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          if (el.classList.contains('parlay-nav-card')) {
            el.classList.add('in');
            viewObserver.unobserve(el);
          } else {
            onscreen.add(el);   // Science spectrum canvas
            tryReveal(el);
          }
        });
      }, { threshold: 0.2 })
    : null;

  /* Layout tracker: a canvas has zero size until the drawer opens. */
  var resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(function (entries) {
        entries.forEach(function (entry) { paint(entry.target); });
      })
    : null;

  function attach(canvas) {
    if (observed.has(canvas)) return;
    observed.add(canvas);
    if (resizeObserver) resizeObserver.observe(canvas);
    if (viewObserver) {
      viewObserver.observe(canvas);
    } else {
      onscreen.add(canvas);   // no IntersectionObserver → treat as always in view
    }
    paint(canvas);
  }

  /* Shop photo cards: no canvas — just add `.in` on scroll-in so CSS runs the
     sheen. The card itself is always visible, so this is purely additive. */
  var navObserved = new WeakSet();
  function attachNavCard(card) {
    if (navObserved.has(card)) return;
    navObserved.add(card);
    if (viewObserver) viewObserver.observe(card);
    else card.classList.add('in');   // no IntersectionObserver → reveal on load
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(CANVAS_SELECTOR).forEach(attach);
    root.querySelectorAll(NAV_CARD_SELECTOR).forEach(attachNavCard);
  }

  function init() {
    scan(document);

    /* Accordion/slide submenus arrive later via the Section Rendering API;
       catch any science canvas injected after first paint. */
    document.querySelectorAll('menu-drawer').forEach(function (drawer) {
      new MutationObserver(function () { scan(drawer); })
        .observe(drawer, { childList: true, subtree: true });
    });

    if (!resizeObserver) {
      var raf;
      var redrawAll = function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          document.querySelectorAll(CANVAS_SELECTOR).forEach(paint);
        });
      };
      window.addEventListener('resize', redrawAll);
      document.addEventListener('click', redrawAll, true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the header section wholesale — re-wire the new DOM. */
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
